import { AthenaImportResults } from "pages/Orders/components/AthenaImportDialog/AthenaImportResults";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { useAthenaImportStatusQuery } from "store/queries/integration";
import { IAthenaImportLogItem } from "types/AthenaIntegration";

import { formatDateTime } from "../../utils/hstImports";

interface IAthenaImportResultsDialogProps {
  item: IAthenaImportLogItem | null;
  onHide: () => void;
}

/**
 * The per-appointment results of one past import — the same view the "Import from Athena" dialog shows
 * when an import finishes. Loaded from `GET /integration/athena/import/:importId`, the endpoint that
 * dialog polls; the list endpoint never carries `results` (patient names).
 */
export function AthenaImportResultsDialog({
  item,
  onHide,
}: IAthenaImportResultsDialogProps) {
  const { data, isFetching, isError } = useAthenaImportStatusQuery(
    item?.id ?? "",
    { skip: !item }
  );
  const record = data?.data;

  const renderContent = () => {
    if (isFetching) {
      return (
        <div className="flex justify-content-center p-5">
          <ProgressSpinner style={{ width: "40px", height: "40px" }} />
        </div>
      );
    }
    if (isError || !record) {
      return <p className="m-0">Could not load this import.</p>;
    }
    if (record.status === "failed") {
      return (
        <p className="m-0 text-red-600">
          This import failed: {record.errorMessage || "no reason recorded"}
        </p>
      );
    }
    if (record.status !== "completed") {
      return <p className="m-0">This import is still running.</p>;
    }
    return (
      <AthenaImportResults importResult={record} showEligibilityNote={false} />
    );
  };

  const header = item
    ? `${item.filename || "Athena import"} — ${formatDateTime(item.createdAt)}`
    : "Athena import";

  return (
    <Dialog
      header={header}
      visible={!!item}
      onHide={onHide}
      style={{ width: "760px", maxWidth: "95vw" }}
    >
      {item ? renderContent() : null}
    </Dialog>
  );
}
